import { lazy, Suspense, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { PlatformSettingsProvider } from './contexts/PlatformSettingsContext';
import { ToastProvider } from './contexts/ToastContext';
import { SessionTimeoutWarning } from './components/SessionTimeoutWarning';
import { isNativePlatform } from './utils/platform';
import { Loader2 } from 'lucide-react';
import { queryClient } from './queryClient';
import './index.css';

// ── StatusBar setup for native platforms ────────────────────────────
// Prevents the WebView from drawing behind the system status bar on
// Android / iOS so content never overlaps the clock / battery icons.
if (isNativePlatform()) {
  import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
    StatusBar.setOverlaysWebView({ overlay: false });
    StatusBar.setBackgroundColor({ color: '#0f172a' });
    StatusBar.setStyle({ style: Style.Dark });
  }).catch(() => {
    // StatusBar plugin not available — ignore
  });
}

// Lazy-load both routers so only the needed one is actually fetched.
// This prevents the full web router (which transitively imports heavy deps
// like recharts) from being loaded inside Capacitor where it isn't used.
const Router = lazy(() =>
  import('./router').then((m) => ({ default: m.Router })),
);
const MobileRouter = lazy(() =>
  import('./router-mobile').then((m) => ({ default: m.MobileRouter })),
);

/**
 * Platform-aware App root.
 *
 * When running inside a Capacitor native shell (Android / iOS),
 * the app renders the MobileRouter which exposes only the 5-6
 * mobile-optimised screens. On the web it renders the full router.
 */
function App() {
  const isMobile = isNativePlatform();

  // Hide the Capacitor splash screen once the React tree has mounted.
  // When loading from a dev server the native auto-hide doesn't always
  // trigger, so we call hide() explicitly via the native bridge.
  useEffect(() => {
    if (!isMobile) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor as
      | { Plugins?: { SplashScreen?: { hide: () => void } } }
      | undefined;
    cap?.Plugins?.SplashScreen?.hide();
  }, [isMobile]);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <PlatformSettingsProvider>
          <WebSocketProvider>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center bg-slate-900">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                </div>
              }
            >
              {isMobile ? <MobileRouter /> : <Router />}
            </Suspense>
            <SessionTimeoutWarning />
          </WebSocketProvider>
        </PlatformSettingsProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
